'use client';

import { useState } from 'react';
import Link from 'next/link';
import toast from 'react-hot-toast';
import { Loader2, Lock, Minus, Plus, Ticket } from 'lucide-react';
import api from '@/app/lib/axios';
import { useAuth } from '@/app/hooks/context/AuthContext';
import { CommunityRaffle, RaffleBuyResponse } from '@/types/raffle';
import { formatDate } from '@/app/raffles/utils';
import { KickAmount, Label } from '@/app/raffles/components/RaffleBits';

interface EntryFormProps {
    raffle: CommunityRaffle;
    onUpdated: (raffle: CommunityRaffle) => void;
    onEntered?: () => void;
}

const QUICK_PICKS = [10, 50, 100, 500];

function Notice({ icon: Icon, children }: { icon: typeof Lock; children: React.ReactNode }) {
    return (
        <div className="flex items-center justify-center gap-2 rounded-xl bg-gray-800/60 py-3 text-xs font-mono font-bold uppercase tracking-wider text-gray-400">
            <Icon className="w-4 h-4" /> {children}
        </div>
    );
}

/** Ticket picker + enter button. 1 ticket = 1 Kick point. */
export default function EntryForm({ raffle, onUpdated, onEntered }: EntryFormProps) {
    const { user, updateBalance } = useAuth();
    const [qty, setQty] = useState(raffle.min_tickets);
    const [submitting, setSubmitting] = useState(false);

    const myTickets = raffle.my_tickets ?? 0;
    const remaining = Math.max(0, raffle.max_tickets - myTickets);
    const balance = user?.points ?? 0;
    const affordable = Math.min(remaining, balance);

    if (raffle.status === 'upcoming') return <Notice icon={Lock}>Entries open {formatDate(raffle.starts_at)}</Notice>;
    if (raffle.status !== 'active') return null;
    if (!user) return <Notice icon={Lock}>Log in with Kick to enter</Notice>;
    if (user.provider !== 'kick') return <Notice icon={Lock}>Kick accounts only</Notice>;
    if (raffle.stake_only && !user.stake_verified_at) {
        return (
            <Link
                href="/stake-role"
                className="flex items-center justify-center gap-2 rounded-xl bg-gray-800/60 hover:bg-gray-800/80 py-3 text-xs font-mono font-bold uppercase tracking-wider text-gray-400 transition-colors"
            >
                <Lock className="w-4 h-4" /> Stake role only — <span className="text-sky-300">Get Stake Role</span>
            </Link>
        );
    }
    if (remaining < raffle.min_tickets) return <Notice icon={Ticket}>Max tickets entered</Notice>;

    const clamp = (n: number) => Math.max(raffle.min_tickets, Math.min(remaining, Math.floor(n) || 0));
    const picks = QUICK_PICKS.filter(n => n > raffle.min_tickets && n < remaining);
    const tooPoor = qty > balance;
    const invalid = qty < raffle.min_tickets || qty > remaining;

    const submit = async () => {
        if (invalid || tooPoor) return;
        setSubmitting(true);
        try {
            const { data } = await api.post<RaffleBuyResponse>(`raffles/${raffle.id}/buy`, { tickets: qty });
            updateBalance(data.points);
            onUpdated(data.raffle);
            toast.success(`${qty.toLocaleString()} ${qty === 1 ? 'ticket' : 'tickets'} entered — good luck!`);
            setQty(raffle.min_tickets);
            onEntered?.();
        } catch (error: unknown) {
            const message = (error as { response?: { data?: { message?: string } } })?.response?.data?.message || 'Could not enter the raffle';
            toast.error(message);
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <div className="space-y-3">
            <div className="flex items-baseline justify-between">
                <Label>Tickets</Label>
                <span className="text-xs font-mono text-gray-400 inline-flex items-center gap-1">
                    Balance: <KickAmount value={balance} size={12} />
                </span>
            </div>

            <div className="flex items-center gap-2 rounded-xl bg-gray-800/60 p-1.5">
                <button
                    type="button"
                    onClick={() => setQty(q => clamp(q - 1))}
                    disabled={qty <= raffle.min_tickets}
                    className="p-2 rounded-lg text-gray-300 hover:bg-white/5 disabled:opacity-30 transition-colors"
                    aria-label="Fewer tickets"
                >
                    <Minus className="w-4 h-4" />
                </button>
                <input
                    type="number"
                    inputMode="numeric"
                    min={raffle.min_tickets}
                    max={remaining}
                    value={qty}
                    onChange={(e) => setQty(Math.floor(Number(e.target.value)) || 0)}
                    onBlur={() => setQty(q => clamp(q))}
                    className="flex-1 min-w-0 bg-transparent text-center text-lg font-mono font-bold text-white tabular-nums outline-none [appearance:textfield] [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none"
                />
                <button
                    type="button"
                    onClick={() => setQty(q => clamp(q + 1))}
                    disabled={qty >= remaining}
                    className="p-2 rounded-lg text-gray-300 hover:bg-white/5 disabled:opacity-30 transition-colors"
                    aria-label="More tickets"
                >
                    <Plus className="w-4 h-4" />
                </button>
            </div>

            <div className="flex flex-wrap gap-1.5">
                {picks.map(n => (
                    <button
                        key={n}
                        type="button"
                        onClick={() => setQty(clamp(n))}
                        className={`flex-1 rounded-lg py-1.5 text-xs font-mono font-bold tabular-nums transition-colors ${
                            qty === n ? 'bg-green-500/15 text-green-400' : 'bg-gray-800/60 text-gray-400 hover:text-white'
                        }`}
                    >
                        {n}
                    </button>
                ))}
                <button
                    type="button"
                    onClick={() => setQty(clamp(affordable))}
                    className={`flex-1 rounded-lg py-1.5 text-xs font-mono font-bold uppercase transition-colors ${
                        qty === clamp(affordable) && affordable >= raffle.min_tickets ? 'bg-green-500/15 text-green-400' : 'bg-gray-800/60 text-gray-400 hover:text-white'
                    }`}
                >
                    Max
                </button>
            </div>

            <button
                onClick={submit}
                disabled={submitting || invalid || tooPoor}
                className="group/btn relative w-full overflow-hidden rounded-xl bg-gradient-to-br from-emerald-600 via-emerald-800 to-gray-900 px-6 py-2.5 font-mono font-bold text-gray-200 shadow-lg hover:shadow-emerald-500/30 transition-all duration-200 disabled:opacity-40 disabled:cursor-not-allowed"
            >
                <span className="relative z-10 flex items-center justify-center gap-2">
                    {submitting ? <><Loader2 className="w-4 h-4 animate-spin" /> ENTERING</> : 'ENTER RAFFLE'}
                </span>
                <span className="absolute inset-0 bg-gradient-to-r from-green-400/0 via-green-400/30 to-green-400/0 -translate-x-full group-hover/btn:translate-x-full transition-transform duration-700" />
            </button>

            <p className="text-center text-[11px] font-mono text-gray-500">
                1 ticket = 1 Kick Point · min {raffle.min_tickets.toLocaleString()} per entry · {remaining.toLocaleString()} more allowed
            </p>
        </div>
    );
}
